import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import getAvatarUrl from "../../../utils/get-avatar-url";
import SvgIcon from "../../SvgIcon";

/**
 * @param {object} props
 * @param {object} props.commentActions - { add, delete, edit } from comment-action-configs
 * @param {object} props.commentParams - Params for buildBody (threadId, etc.)
 */
export const CommentsList = ({
  lastCommentRef,
  locals,
  threadContainerRef,
  commentActions,
  commentParams = {},
  photoId,
  isMealPhotos,
}) => {
  const { t } = useTranslation("common");
  const { athlete, activeUser } = locals;
  const comments = isMealPhotos
    ? locals.mealComments?.[photoId] ?? []
    : locals.comments;
  const [commentEdition, setCommentEdition] = useState(false);

  const handleDeleteComment = (commentId) => {
    if (!window.confirm(t("threadComments.deleteConfirm")))
      return;
    if (!commentActions?.delete) return;
    const notDeletedCommentsCount = comments.filter(
      (comment) => !comment?.isDeleted
    )?.length;
    const fullDelete = notDeletedCommentsCount === 1;
    const { url, buildBody } = commentActions.delete;
    submit(typeof url === "function" ? url(commentId) : url, {
      method: "POST",
      body: buildBody(commentParams, { fullDelete }),
    });
  };

  const handleEditComment = (commentId, message) => {
    setCommentEdition({ _id: commentId, message });
  };

  const handleSaveComment = () => {
    if (!commentActions?.edit || !commentEdition) return;
    const { url, buildBody } = commentActions.edit;
    submit(url, {
      method: "POST",
      body: buildBody(commentParams, {
        commentId: commentEdition._id,
        message: commentEdition.message,
      }),
    });
    setCommentEdition(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveComment();
    }
  };

  return (
    <div
      className={`thread-container${comments?.length > 2 ? " shadowed" : ""}`}
      ref={threadContainerRef}
    >
      {comments?.map((comment, index) => {
        const { message, senderId } = comment;
        const commentUser =
          comment?.sender ??
          [athlete, activeUser].find((user) => user?._id === senderId);
        const avatarUrl = getAvatarUrl(commentUser);
        return (
          <div
            {...(index === comments?.length - 1 && {
              ref: lastCommentRef,
            })}
            key={comment?._id}
            className={`thread-comment-wrapper${
              index === 0 && comments?.length > 2 ? " first" : ""
            }`}
          >
            <img
              className={`thread-comment-avatar${
                comment?.isDeleted ? " deleted" : ""
              }`}
              src={avatarUrl}
              alt={t("threadComments.userAvatarAria")}
              loading="lazy"
            />
            <div className="thread-comment-content">
              <span
                className={`thread-comment-username${
                  comment?.isDeleted ? " deleted" : ""
                }`}
              >
                {commentUser?.name}
              </span>
              {comment?.isDeleted ? (
                <span className="thread-comment-message deleted">
                  {t("threadComments.deletedByAuthor")}
                </span>
              ) : commentEdition?._id === comment?._id ? (
                <textarea
                  rows={1}
                  name="thread-comment-edit"
                  className="thread-comment-edit-input"
                  value={commentEdition?.message}
                  onChange={(e) =>
                    setCommentEdition({
                      ...commentEdition,
                      message: e.target.value,
                    })
                  }
                  onKeyDown={handleKeyDown}
                />
              ) : (
                <p className="thread-comment-message">
                  {message}
                  {comment?.editedAt && (
                    <span className="thread-comment-message-edited">
                      {" "}
                      {t("threadComments.edited")}
                    </span>
                  )}
                </p>
              )}
            </div>
            {!comment?.isDeleted &&
              comment?.senderId === locals?.activeUser?._id && (
                <div className="thread-comment-actions">
                  {commentEdition?._id === comment?._id ? (
                    <button
                      type="button"
                      data-button-type="primary"
                      className="thread-comment-action-save"
                      onClick={handleSaveComment}
                    >
                      <SvgIcon
                        src={"/img/new-icons/check.svg"}
                        noFilterOnLight={true}
                      />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="thread-comment-action-edit"
                      onClick={() => handleEditComment(comment?._id, message)}
                    >
                      <SvgIcon
                        src={"/img/new-icons/edit-pencil.svg"}
                        noFilterOnLight={true}
                      />
                    </button>
                  )}
                  {commentEdition?._id === comment?._id ? (
                    <button
                      type="button"
                      className="thread-comment-action-cancel"
                      onClick={() => setCommentEdition(null)}
                    >
                      <SvgIcon
                        src={"/img/new-icons/red-cross-20px.svg"}
                        noFilterOnLight={true}
                      />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="thread-comment-action-delete"
                      onClick={() => handleDeleteComment(comment?._id)}
                    >
                      <SvgIcon
                        src="/img/new-icons/red-trash.svg"
                        noFilterOnLight={true}
                      />
                    </button>
                  )}
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
};
